// TEAM RequestService — REST API only (web-v2).
import { restFetch } from "../../api/restClient";

export async function fetchAccounts() {
  try {
    const data = await restFetch("/v1/accounts");
    return data.accounts;
  } catch (err) {
    console.log("error fetching accounts");
  }
}

export async function fetchPermissions() {
  try {
    const data = await restFetch("/v1/permissions");
    return data?.permissions || data || [];
  } catch (err) {
    console.log("error fetching permissions", err);
  }
}

export async function getMgmtAccountPs() {
  try {
    const data = await restFetch("/v1/permissions");
    return data?.permissions || [];
  } catch (err) {
    console.log("error fetching permissions");
  }
}

export async function getUserRequests(email) {
  try {
    const qs = new URLSearchParams({ email });
    const res = await restFetch(`/v1/requests?${qs.toString()}`);
    return res.items || [];
  } catch (err) {
    console.log("error fetching requests");
    return { error: err };
  }
}

export async function fetchOUs() {
  try {
    const data = await restFetch("/v1/ous");
    return data?.ous;
  } catch (err) {
    console.log("error fetching OUs");
    return { error: err };
  }
}

export async function fetchOU(_id) {
  // Account-level Approver policies cover submit checks.
  return null;
}

export async function getGroupMemberships(id) {
  try {
    const data = await restFetch("/v1/groups", {
      method: "POST",
      body: { groupIds: id },
    });
    return data || { members: [] };
  } catch (err) {
    console.error("[TEAM] getGroupMemberships error", err);
    return { members: [] };
  }
}

export async function fetchIdCGroups() {
  try {
    const data = await restFetch("/v1/idc-groups");
    return data?.groups || [];
  } catch (err) {
    console.log("error fetching IdC Groups");
  }
}

export async function fetchUsers() {
  try {
    const data = await restFetch("/v1/users");
    return data?.users || [];
  } catch (err) {
    console.log("error fetching users");
  }
}

export async function getSessionList() {
  try {
    const res = await restFetch("/v1/requests?all=1");
    return res.items || [];
  } catch (err) {
    console.log("error fetching sessions");
    return { error: err };
  }
}

export async function getRequest(id) {
  try {
    return await restFetch(`/v1/requests/${id}`);
  } catch (err) {
    console.log("error fetching request");
  }
}

export async function getAllApprovers() {
  try {
    const res = await restFetch("/v1/approvers");
    return res.items || [];
  } catch (err) {
    console.log("error fetching approvers");
    return { error: err };
  }
}

function _walkFilter(node, acc) {
  if (!node || typeof node !== "object") return;
  if (Array.isArray(node)) {
    node.forEach((n) => _walkFilter(n, acc));
    return;
  }
  if (node.and) _walkFilter(node.and, acc);
  if (node.or) _walkFilter(node.or, acc);
  if (node.status && node.status.eq) acc.statuses.push(node.status.eq);
  if (node.email && node.email.eq) acc.emailEq = node.email.eq;
  if (node.email && node.email.ne) acc.emailNe = node.email.ne;
  if (node.approvers && node.approvers.contains) {
    acc.approversContains = node.approvers.contains;
  }
  if (node.approver && node.approver.eq) acc.approverEq = node.approver.eq;
}

function _uniqById(items) {
  const map = new Map();
  for (const item of items || []) {
    if (item && item.id) map.set(item.id, item);
  }
  return [...map.values()];
}

/** sessions() accepts the UI filter object shape used by Active/Audit pages. */
export async function sessions(filter) {
  try {
    const acc = {
      statuses: [],
      emailEq: null,
      emailNe: null,
      approversContains: null,
      approverEq: null,
    };
    _walkFilter(filter, acc);
    const statuses = [...new Set(acc.statuses)];

    if (acc.approverEq && !acc.emailEq && !acc.approversContains) {
      const approverId = `idc_${String(acc.approverEq).toLowerCase()}`;
      const res = await restFetch(
        `/v1/requests?approverId=${encodeURIComponent(approverId)}`
      );
      return Array.isArray(res?.items) ? res.items : [];
    }

    if (acc.approversContains && acc.emailNe && !acc.emailEq) {
      const status = statuses[0] || "pending";
      const qs = new URLSearchParams({
        status,
        approverEmail: acc.approversContains,
        excludeEmail: acc.emailNe,
      });
      const res = await restFetch(`/v1/requests?${qs.toString()}`);
      return Array.isArray(res?.items) ? res.items : [];
    }

    let items = [];
    if (acc.emailEq) {
      if (statuses.length > 0) {
        for (const st of statuses) {
          const qs = new URLSearchParams({ email: acc.emailEq, status: st });
          const res = await restFetch(`/v1/requests?${qs.toString()}`);
          items = items.concat(Array.isArray(res?.items) ? res.items : []);
        }
      } else {
        const qs = new URLSearchParams({ email: acc.emailEq });
        const res = await restFetch(`/v1/requests?${qs.toString()}`);
        items = items.concat(Array.isArray(res?.items) ? res.items : []);
      }
    }
    if (acc.approversContains) {
      const stList = statuses.length > 0 ? statuses : [null];
      for (const st of stList) {
        const qs = new URLSearchParams({
          approverEmail: acc.approversContains,
        });
        if (st) qs.set("status", st);
        if (acc.emailNe) qs.set("excludeEmail", acc.emailNe);
        const res = await restFetch(`/v1/requests?${qs.toString()}`);
        items = items.concat(Array.isArray(res?.items) ? res.items : []);
      }
    }

    if (!acc.emailEq && !acc.approversContains && !acc.approverEq) {
      const res = await restFetch("/v1/requests?all=1");
      return Array.isArray(res?.items) ? res.items : [];
    }

    items = _uniqById(items);
    if (statuses.length > 0) {
      items = items.filter((i) => statuses.includes(i.status));
    }
    return items;
  } catch (err) {
    console.error("[TEAM] sessions fetch error", err);
    return [];
  }
}

export async function fetchLogs(args) {
  try {
    const qs = new URLSearchParams({ queryId: args.queryId || "" });
    const data = await restFetch(`/v1/logs?${qs.toString()}`);
    return Array.isArray(data?.logs) ? data.logs : Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("[TEAM] fetchLogs error", err);
    return [];
  }
}

export async function fetchPolicy(args) {
  try {
    const data = await restFetch("/v1/policy", {
      method: "POST",
      body: {
        userId: args.userId || "",
        groupIds: args.groupIds || [],
        email: args.email || "",
      },
    });
    return data;
  } catch (err) {
    console.error("[TEAM] fetchPolicy error", err);
    throw err;
  }
}

export async function updateStatus(data) {
  try {
    const { id, ...patch } = data;
    return await restFetch(`/v1/requests/${id}`, { method: "PATCH", body: patch });
  } catch (err) {
    console.log("error updating status");
  }
}

export async function requestTeam(data) {
  try {
    const created = await restFetch("/v1/requests", { method: "POST", body: data });
    return created.id;
  } catch (err) {
    console.log("error creating request");
  }
}

export async function getSessionLogs(data) {
  try {
    const created = await restFetch("/v1/sessions", { method: "POST", body: data });
    return created.id;
  } catch (err) {
    console.log("error creating session Logs");
  }
}

export async function deleteSessionLogs(data) {
  try {
    return await restFetch(`/v1/sessions/${data.id}`, { method: "DELETE" });
  } catch (err) {
    console.log("error deleting session log");
  }
}

export async function getSession(id) {
  try {
    return await restFetch(`/v1/sessions/${encodeURIComponent(id)}`);
  } catch (err) {
    console.debug("[TEAM] getSession miss/error", { id, err: String(err) });
    return null;
  }
}

export async function addApprovers(data) {
  try {
    const created = await restFetch("/v1/approvers", { method: "POST", body: data });
    return created.id;
  } catch (err) {
    console.log("error adding Approvers");
  }
}

export async function delApprover(data) {
  try {
    return await restFetch(`/v1/approvers/${data.id}`, { method: "DELETE" });
  } catch (err) {
    console.log("error deleting approver");
  }
}

export async function editApprover(data) {
  try {
    return await restFetch(`/v1/approvers/${data.id}`, { method: "PATCH", body: data });
  } catch (err) {
    console.log("error updating approver");
  }
}

export async function fetchApprovers(id, _type) {
  try {
    return await restFetch(`/v1/approvers/${encodeURIComponent(id)}`);
  } catch (err) {
    console.debug("[TEAM] fetchApprovers miss/error", { id, err: String(err) });
    return null;
  }
}

export async function addPolicy(data) {
  try {
    const created = await restFetch("/v1/eligibility", { method: "POST", body: data });
    return created.id;
  } catch (err) {
    console.log("error creating policy");
  }
}

export async function delPolicy(data) {
  try {
    return await restFetch(`/v1/eligibility/${data.id}`, { method: "DELETE" });
  } catch (err) {
    console.log("error deleting policy");
  }
}

export async function editPolicy(data) {
  try {
    return await restFetch(`/v1/eligibility/${data.id}`, { method: "PATCH", body: data });
  } catch (err) {
    console.log("error updating policy");
  }
}

export async function fetchEligibility(id) {
  try {
    return await restFetch(`/v1/eligibility/${id}`);
  } catch (err) {
    console.log("error fetching eligibility");
  }
}

export async function getAllEligibility() {
  try {
    const res = await restFetch("/v1/eligibility");
    return res.items || [];
  } catch (err) {
    console.log("error fetching eligibility");
    return { error: err };
  }
}

export async function getSetting(id) {
  try {
    return await restFetch(`/v1/settings/${id}`);
  } catch (err) {
    if (String(err.message || err).includes(" 404 ")) {
      return null;
    }
    console.log("error fetching settings", err);
    return null;
  }
}

export async function createSetting(data) {
  try {
    const created = await restFetch(`/v1/settings/${data.id || "settings"}`, {
      method: "PUT",
      body: data,
    });
    return created.id;
  } catch (err) {
    console.log("error creating settings");
  }
}

export async function updateSetting(data) {
  try {
    return await restFetch(`/v1/settings/${data.id || "settings"}`, {
      method: "PATCH",
      body: data,
    });
  } catch (err) {
    console.log("error updating settings");
  }
}

export async function revokePim(data) {
  try {
    await updateStatus(data);
  } catch (err) {
    console.log("error revoking request");
  }
}

export async function invalidateOUCache(ouIds) {
  console.warn("[TEAM] invalidateOUCache: not available over REST", ouIds);
  return { invalidated: [], failed: ouIds || [], message: "not_implemented" };
}

export async function validateRequest(accountId, roleId, userId, groupIds, email) {
  try {
    return await restFetch("/v1/requests/validate", {
      method: "POST",
      body: {
        accountId,
        roleId,
        userId: userId || "",
        groupIds: groupIds || [],
        email: email || "",
      },
    });
  } catch (err) {
    console.log("error validating request", err);
    throw err;
  }
}

export async function fetchOUAccounts(ouIds) {
  try {
    return await restFetch("/v1/ous/accounts", {
      method: "POST",
      body: { ouIds },
    });
  } catch (err) {
    console.log("error fetching OU accounts", err);
    throw err;
  }
}
